import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faCardsBlank } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  HeadlineL,
  HeadlineM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { FOOTBALL_BUNDLED_AUCTIONS_ID } from '@sorare/core/src/constants/__generated__/routes';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import type { Props as UseAmountWithConversionProps } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary } from '@sorare/core/src/lib/glossary';

import { AuctionReminder } from '#src/components/auction/AuctionReminder/index.tsx';
import { AuctionTimeLeft } from '#src/components/auction/AuctionTimeLeft/index.tsx';
import { useBestBidBelongsToUser } from '#src/hooks/auctions/useBestBidBelongsToUser.ts';

import type { BidInfos_auction } from './__generated__/index.graphql.ts';

type Props = {
  auction: BidInfos_auction;
  withReminder?: boolean;
  showBundleContext?: boolean;
};

const Row = styled.div`
  display: flex;
  gap: var(--quadruple-unit);
`;

const Line = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: var(--unit);
  align-items: baseline;
`;

const StyledText14 = styled(BodyS)`
  display: flex;
  gap: var(--half-unit);
  flex-wrap: wrap;
`;

const AmountWithConversion = ({
  bigger = false,
  ...props
}: UseAmountWithConversionProps & { bigger?: boolean }) => {
  const { main, exponent } = useAmountWithConversion(props);
  if (bigger) {
    return (
      <Line>
        <HeadlineL as="h3" color="var(--c-white)">
          {main}
        </HeadlineL>
        <BodyM color="var(--c-nd-600)">{exponent}</BodyM>
      </Line>
    );
  }
  return (
    <Line>
      <HeadlineM as="h4" color="var(--c-white)">
        {main}
      </HeadlineM>
      <BodyS color="var(--c-nd-600)">{exponent}</BodyS>
    </Line>
  );
};

const BidInfosTitle = ({ auction }: Pick<Props, 'auction'>) => {
  const bestBidBelongsToUser = useBestBidBelongsToUser();

  const { open, bestBid, myLastBid, cancelled } = auction;

  if (!open || !bestBid || cancelled || !myLastBid) return null;

  if (bestBidBelongsToUser(bestBid))
    return (
      <HeadlineM as="h4" color="var(--c-white)">
        <FormattedMessage defaultMessage="You are the highest bidder" />
      </HeadlineM>
    );
  return (
    <HeadlineM as="h4" color="var(--c-red-400)">
      <FormattedMessage defaultMessage="You’ve been outbid" />
    </HeadlineM>
  );
};

const TimelineRow = styled(Line)`
  justify-content: space-between;
  align-items: center;
`;

const AuctionTimelineInformation = ({ auction }: Props) => {
  const { bidsCount, cancelled, endDate } = auction;
  return (
    <StyledText14 as="div" color="var(--c-nd-600)">
      {cancelled ? (
        <FormattedMessage {...glossary.canceled} />
      ) : (
        <FormattedMessage
          defaultMessage="{bidsCount, plural, =0 {# bid • } one { # bid • } other {# bids • }}{timeleft}"
          values={{
            bidsCount,
            timeleft: <AuctionTimeLeft endDate={endDate} forceInlineLayout />,
          }}
        />
      )}
    </StyledText14>
  );
};

const UnderlineLink = styled(Link)`
  text-decoration: underline;
`;

export const BidInfos = ({
  auction,
  withReminder,
  showBundleContext,
}: Props) => {
  const { bestBid, currentPrice, currency, myBestBid, myLastBid } = auction;
  const { toMonetaryAmount, toMonetaryAmountInReferenceCurrency } =
    useMonetaryAmount();

  const bestBidAmount = bestBid && toMonetaryAmount(bestBid.amounts);

  const myBestBidMaxAmount =
    myBestBid && toMonetaryAmount(myBestBid.maximumAmounts);

  const bestBidIsMaxBid =
    bestBidAmount && myBestBidMaxAmount?.isEqualTo(bestBidAmount);

  return (
    <Vertical gap={2}>
      {auction.cards.length > 1 && !!showBundleContext && (
        <Horizontal>
          <FontAwesomeIcon size="1x" icon={faCardsBlank} />
          <LabelS>
            <FormattedMessage
              defaultMessage="Sold in a <link>bundle</link>"
              values={{
                link: children => (
                  <UnderlineLink
                    to={generatePath(FOOTBALL_BUNDLED_AUCTIONS_ID, {
                      id: idFromObject(auction.id),
                    })}
                  >
                    {children}
                  </UnderlineLink>
                ),
              }}
            />
          </LabelS>
        </Horizontal>
      )}
      <BidInfosTitle auction={auction} />
      <Row>
        <Vertical>
          {!!myLastBid && (
            <BodyM color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Current bid" />
            </BodyM>
          )}
          {!!currentPrice && (
            <AmountWithConversion
              monetaryAmount={toMonetaryAmountInReferenceCurrency(
                currentPrice,
                currency
              )}
              bigger={!myLastBid}
            />
          )}
        </Vertical>
        {!!myBestBidMaxAmount && !bestBidIsMaxBid && (
          <Vertical>
            <BodyM color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Your max bid" />
            </BodyM>
            <AmountWithConversion monetaryAmount={myBestBidMaxAmount} />
          </Vertical>
        )}
      </Row>
      <TimelineRow>
        <AuctionTimelineInformation auction={auction} />
        {!!withReminder && (
          <AuctionReminder color="tertiary" auction={auction} />
        )}
      </TimelineRow>
    </Vertical>
  );
};

BidInfos.fragments = {
  auction: gql`
    fragment BidInfos_auction on TokenAuction {
      id
      bidsCount
      open
      endDate
      cancelled
      currentPrice
      currency
      privateCurrentPrice
      cards: anyCards {
        slug
        sport
      }
      bestBid {
        id
        amounts {
          ...useMonetaryAmount_monetaryAmount
        }
        ...UseBestBidBelongsToUser_bestBid
      }
      myBestBid {
        id
        maximumAmounts {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      myLastBid {
        id
      }
      ...AuctionReminder_auction
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${useBestBidBelongsToUser.fragments.bestBid}
    ${AuctionReminder.fragments.auction}
  ` as TypedDocumentNode<BidInfos_auction>,
};
